// Map a trace's fired-signal `loc` regions onto its rendered turn blocks, so the
// trace-detail view can highlight exactly where each signal fired.
//
// A fired verdict carries `value.locs = [{surface, index, start, end}]` (see
// triggerHarness.tagsToAnnotation + the signals' `loc` emission). Each loc's
// offsets index into the EXACT surface string the signal scanned, which we
// reconstruct here via `traceSurfaces` (same turnText/toContentString the
// signal saw). The trace detail scans the WHOLE trace (traceScanSurfaces), so
// `traceSurfaces` — not the newest-turn-only eventSurfaceStrings — is the
// matching mapper: its tool_result ordering (every turn, document order) is
// what the scan's loc.index refers to. We slice out the matched substring and key it to the rendered
// block by (turnIndex, blockIndex); the detail's CollapsibleText then scoped-
// searches that substring within the block and marks it. Searching the matched
// SUBSTRING (rather than trusting raw offsets against the formatted DOM) is
// robust to the detail's own flattening and to multi-text-block user turns.
//
// Redaction-safe: the verdict only carries offsets + a redacted display value;
// the raw matched substring is sliced from the trace's own (already-rendered)
// surface string here, client-side — it adds no leak the detail doesn't already
// show. Tool-call locs may additionally carry a non-secret argument field name
// so the mapper can slice a command without carrying the credential itself.

import { traceSurfaces } from "./triggers/promptSurfaces.js";
import { toolResultText } from "./traceToolCall.js";

const keyOf = (turnIndex, blockIndex) => `${turnIndex}:${blockIndex}`;

// Keep the tool-input serializer shared with ContextPanel. A credential loc is
// field-relative, while the rendered block contains every argument; using one
// serializer here and in the component lets us carry the detector's exact field
// span instead of re-searching the first identical value in another argument.
export function toolUseProjection(input) {
    if (input == null) return { text: "", segments: [] };
    if (typeof input !== "object") {
        const text = String(input);
        return { text, segments: [{ fieldPath: "", start: 0, end: text.length }] };
    }
    const parts = [];
    const segments = [];
    for (const [key, value] of Object.entries(input)) {
        if (typeof value === "string") {
            const prefix = `${key}: `;
            const start = parts.join("\n").length + (parts.length ? 1 : 0) + prefix.length;
            parts.push(`${prefix}${value}`);
            const escaped = key.replace(/~/g, "~0").replace(/\//g, "~1");
            segments.push({ fieldPath: `/${escaped}`, start, end: start + value.length });
        }
        else if (value == null || typeof value !== "object") parts.push(`${key}: ${String(value)}`);
        else parts.push(`${key}: ${JSON.stringify(value)}`);
    }
    return { text: parts.join("\n"), segments };
}

export function toolUseText(input) {
    return toolUseProjection(input).text;
}

function toolUseFieldOffset(input, field) {
    if (!input || typeof input !== "object" || typeof field !== "string") return null;
    let offset = 0;
    for (const [key, value] of Object.entries(input)) {
        let line;
        if (typeof value === "string") {
            const prefix = `${key}: `;
            line = `${prefix}${value}`;
            if (key === field) return offset + prefix.length;
        } else if (value == null || typeof value !== "object") {
            line = `${key}: ${String(value)}`;
        } else {
            line = `${key}: ${JSON.stringify(value)}`;
        }
        offset += line.length + 1;
    }
    return null;
}

// → Map<"turnIndex:blockIndex", [{ match, type }]>
export function buildHighlightMap(turns, fired) {
    const map = new Map();
    if (!Array.isArray(turns) || !Array.isArray(fired) || fired.length === 0) return map;
    const surfaces = traceSurfaces(turns);

    const add = (turnIndex, blockIndex, match, type, rule, command, anchor = null, detail = null, confidence = null, judge = null) => {
        if (turnIndex == null || turnIndex < 0 || blockIndex == null || blockIndex < 0 || !match) return;
        const k = keyOf(turnIndex, blockIndex);
        const arr = map.get(k) || [];
        // de-dupe identical (match,type) so two signals matching the same span
        // don't stack marks. `command` (the fired signal) + `rule` (per-check id,
        // set by the token-efficiency signal) ride along so the trace detail can
        // tooltip WHICH signal/rule matched — the annotation `type` only carries
        // the FIRST fired tag. `detail` (loc.detail, set by the unicode-smuggling
        // signal) is the finding's own text for the tooltip: an invisible-run mark
        // has no visible matched text to speak for itself.
        const start = Number.isInteger(anchor?.start) ? anchor.start : null;
        const end = Number.isInteger(anchor?.end) ? anchor.end : null;
        if (!arr.some((e) => e.match === match && e.type === type
            && (start == null || (e.start === start && e.end === end)))) {
            const e = { match, type };
            if (rule) e.rule = rule;
            if (command) e.command = command;
            if (typeof detail === "string" && detail) e.detail = detail;
            if (typeof confidence === "number" && Number.isFinite(confidence)) e.confidence = confidence;
            // The judge's verdict rides beside the verifier score for the same mark.
            if (judge && typeof judge === "object") e.judge = judge;
            if (start != null && end != null) {
                e.start = start;
                e.end = end;
            }
            arr.push(e);
        }
        map.set(k, arr);
    };

    // Text-block indices of a turn — a user_text loc's offsets are into the
    // turn's joined text (turnText), so the matched substring lives in one of
    // these blocks; attach to all and let the per-block search find it.
    const textBlockIndices = (ti) => {
        const blocks = (turns[ti] && turns[ti].blocks) || [];
        const out = [];
        blocks.forEach((b, bi) => { if (b && b.type === "text") out.push(bi); });
        return out;
    };

    // loc offsets are CPython CODE-POINT indices (python `m.start()/m.end()` over
    // a str of code points); JS string indices are UTF-16 code UNITS. Slice by
    // code point (Array.from splits on surrogate pairs) so astral input — the
    // unicode-smuggling Tag block U+E0000–E007F, or an emoji before the match —
    // doesn't shift the extracted substring. The result is then plain-searched in
    // the rendered (UTF-16) text by markRanges, which is unit-agnostic.
    const slice = (s, loc) => (typeof s === "string" && typeof loc.start === "number" && typeof loc.end === "number"
        ? Array.from(s).slice(loc.start, loc.end).join("") : "");

    // A tool_call loc's `index` is the i-th tool_use block of the LAST turn that
    // has tool_use blocks — mirroring triggerHarness.lastToolCalls / lastTurnBlocks,
    // which is where the event's `tool_calls` come from. Reconstruct that turn and
    // its tool_use block indices here so the mark lands on the rendered call.
    // Most action signals carry a non-secret loc.match. Credential locs instead
    // carry loc.field and offsets, so the raw secret is sliced from the trace's
    // already-visible tool input and never rides in the verdict.
    let toolUseTurn = -1;
    let toolUseBlockIdx = [];
    for (let ti = turns.length - 1; ti >= 0; ti--) {
        const blocks = Array.isArray(turns[ti]?.blocks) ? turns[ti].blocks : [];
        const idxs = [];
        blocks.forEach((b, bi) => { if (b && b.type === "tool_use") idxs.push(bi); });
        if (idxs.length) { toolUseTurn = ti; toolUseBlockIdx = idxs; break; }
    }
    // tool_def blocks are the offered-tool definitions rendered under the system
    // prompt (ContextPanel appends them, in event.tool_defs order). loc.index is
    // the i-th definition; find the turn holding them and map to its i-th tool_def
    // block. loc.match rides the loc (the signal scanned description + schema_text,
    // not the rendered block).
    let toolDefTurn = -1;
    let toolDefBlockIdx = [];
    for (let ti = 0; ti < turns.length; ti++) {
        const blocks = Array.isArray(turns[ti]?.blocks) ? turns[ti].blocks : [];
        const idxs = [];
        blocks.forEach((b, bi) => { if (b && b.type === "tool_def") idxs.push(bi); });
        if (idxs.length) { toolDefTurn = ti; toolDefBlockIdx = idxs; break; }
    }

    // Which turn a tool_call loc belongs to. `loc.index` is the i-th tool_use
    // block OF THE TURN THE SIGNAL SCANNED, and the signal scanned the hydrated
    // event's calls — the last tool-calling turn of that EVENT. The rendered
    // trace can carry later turns, so resolving the index against the rendered
    // trace's last tool-calling turn points at a different turn's i-th call.
    //
    // Nothing detects that today: `add()` only needs a non-empty match, so a
    // wrong block still counts as a highlight and then renders nothing, because
    // the detail scope-searches the match inside a block that lacks it.
    //
    // So the index is used as it was meant — a position WITHIN a turn — and the
    // turn is the one whose i-th call actually renders the match. Walked newest
    // first: the signal fired on the newest turn that matches. Only applies when
    // the loc carries a literal `match`; a credential loc carries offsets and a
    // field instead and keeps the direct path below.
    const toolUseTurnsDesc = [];
    for (let ti = turns.length - 1; ti >= 0; ti--) {
        const blocks = Array.isArray(turns[ti]?.blocks) ? turns[ti].blocks : [];
        const idxs = [];
        blocks.forEach((b, bi) => { if (b && b.type === "tool_use") idxs.push(bi); });
        if (idxs.length) toolUseTurnsDesc.push([ti, idxs]);
    }
    const rendersMatch = (ti, bi, match) => {
        const input = turns[ti]?.blocks?.[bi]?.input;
        return typeof input === "string"
            ? input.includes(match)
            : toolUseText(input).includes(match);
    };
    const locateToolCall = (loc, match) => {
        // the i-th call of the newest turn whose i-th call renders the match
        for (const [ti, idxs] of toolUseTurnsDesc) {
            const bi = idxs[loc.index];
            if (bi != null && rendersMatch(ti, bi, match)) return [ti, bi];
        }
        // otherwise any call that renders it, newest first — the index was not
        // usable, but a block that visibly contains the match still marks the
        // right thing
        for (const [ti, idxs] of toolUseTurnsDesc) {
            for (let k = idxs.length - 1; k >= 0; k--) {
                if (rendersMatch(ti, idxs[k], match)) return [ti, idxs[k]];
            }
        }
        return null;
    };

    for (const v of fired) {
        const locs = v && v.value && v.value.locs;
        if (!Array.isArray(locs)) continue;
        for (const loc of locs) {
            if (!loc || typeof loc.surface !== "string") continue;
            if (loc.surface === "user_text") {
                const match = slice(surfaces.userText.text, loc);
                if (!match) continue;
                for (const bi of textBlockIndices(surfaces.userText.turnIndex)) add(surfaces.userText.turnIndex, bi, match, v.type, undefined, v.command, null, loc.detail, v.confidence, v.judge);
            } else if (loc.surface === "tool_result") {
                // Credential highlight recovery carries current-event block
                // provenance. Prefer it over the legacy whole-trace index so an
                // identical value in an older result cannot steal the mark.
                const anchoredBlock = Number.isInteger(loc.turn) && Number.isInteger(loc.block)
                    ? turns[loc.turn]?.blocks?.[loc.block]
                    : null;
                const tr = anchoredBlock?.type === "tool_result"
                    ? {
                        text: toolResultText(anchoredBlock.content),
                        turnIndex: loc.turn,
                        blockIndex: loc.block,
                    }
                    : surfaces.toolResults[loc.index];
                if (!tr) continue;
                const match = typeof loc.match === "string" ? loc.match : slice(tr.text, loc);
                const anchor = Number.isInteger(loc.start) && Number.isInteger(loc.end)
                    && tr.text.slice(loc.start, loc.end) === match
                    ? { start: loc.start, end: loc.end }
                    : null;
                add(tr.turnIndex, tr.blockIndex, match, v.type, undefined, v.command, anchor, loc.detail, v.confidence, v.judge);
            } else if (loc.surface === "block") {
                // Block-anchored loc: the signal already resolved (turn, block) and
                // carries the matched substring directly (a code token / file path,
                // not a redacted secret), so no surface reconstruction is needed.
                // Used by the experimental token-efficiency signal, which anchors to
                // tool_use blocks the flat user_text/tool_result surfaces don't cover.
                if (typeof loc.turn === "number" && typeof loc.block === "number" && typeof loc.match === "string") {
                    add(loc.turn, loc.block, loc.match, v.type, loc.rule, v.command, loc, loc.detail, v.confidence, v.judge);
                }
            } else if (loc.surface === "tool_call") {
                const anchored = Number.isInteger(loc.turn) && Number.isInteger(loc.block)
                    && turns[loc.turn]?.blocks?.[loc.block]?.type === "tool_use";
                const turnIndex = anchored ? loc.turn : toolUseTurn;
                const bi = anchored ? loc.block : toolUseBlockIdx[loc.index];
                const block = turnIndex >= 0 && bi != null ? turns[turnIndex]?.blocks?.[bi] : null;
                const input = block?.input;
                const rendered = toolUseText(input);
                const persistedAnchor = typeof loc.match === "string"
                    && Number.isInteger(loc.start) && Number.isInteger(loc.end)
                    && rendered.slice(loc.start, loc.end) === loc.match;
                const commandText = typeof input === "string"
                    ? input
                    : (typeof loc.field === "string" && typeof input?.[loc.field] === "string" ? input[loc.field] : "");
                const match = typeof loc.match === "string" ? loc.match : slice(commandText, loc);
                // A literal match can be verified against what the block renders,
                // so a loc whose index resolved to the wrong turn is repaired
                // rather than left to render nothing. Offsets-only locs (a
                // credential's field span) cannot be re-found this way and keep
                // the direct resolution.
                if (!anchored && typeof loc.match === "string" && match) {
                    const found = (bi != null && toolUseTurn >= 0 && rendersMatch(toolUseTurn, bi, match))
                        ? [toolUseTurn, bi]
                        : locateToolCall(loc, match);
                    if (found) add(found[0], found[1], match, v.type, undefined, v.command, null, loc.detail, v.confidence, v.judge);
                } else if (turnIndex >= 0 && bi != null && match) {
                    if (persistedAnchor) {
                        add(turnIndex, bi, match, v.type, undefined, v.command, loc, loc.detail, v.confidence, v.judge);
                        continue;
                    }
                    const fieldOffset = typeof input === "string" ? 0 : toolUseFieldOffset(input, loc.field);
                    const prefix = typeof loc.start === "number"
                        ? Array.from(commandText).slice(0, loc.start).join("")
                        : null;
                    const renderedStart = fieldOffset != null && prefix != null ? fieldOffset + prefix.length : null;
                    const renderedEnd = renderedStart != null ? renderedStart + match.length : null;
                    const anchor = renderedStart != null && rendered.slice(renderedStart, renderedEnd) === match
                        ? { start: renderedStart, end: renderedEnd }
                        : null;
                    add(turnIndex, bi, match, v.type, undefined, v.command, anchor, loc.detail, v.confidence, v.judge);
                }
            } else if (loc.surface === "tool_def") {
                const bi = toolDefBlockIdx[loc.index];
                if (toolDefTurn >= 0 && bi != null && typeof loc.match === "string") {
                    add(toolDefTurn, bi, loc.match, v.type, undefined, v.command, null, loc.detail, v.confidence, v.judge);
                }
            }
        }
    }
    return map;
}

// Where a mark lands in `text`: its validated offset when that still slices to
// the match, otherwise the match's first occurrence. The mark's stored offset
// was computed against the surface the signal scanned, which is not always the
// string the block finally renders (a tool input shown by a different serializer
// than the one the offset came from), so the offset is trusted only when it
// still checks out. Returns -1 when the match is absent from `text`.
export function markOffset(text, m) {
    const needle = m && m.match;
    if (typeof text !== "string" || !needle) return -1;
    const anchored = Number.isInteger(m.start) && Number.isInteger(m.end)
        && m.start >= 0 && m.end >= m.start && m.end <= text.length
        && text.slice(m.start, m.end) === needle;
    return anchored ? m.start : text.indexOf(needle);
}

// The mark whose rendered range starts at `start` for tag `type`. The trace
// detail recovers the full entry (command/rule/detail, for the tooltip) from the
// bare (start,type) a range carries; it MUST decide placement exactly as
// markRanges did, or a mark whose offset no longer matches the rendered text
// renders without its tooltip. Same `markOffset` on both sides guarantees that.
export function markEntryAt(matches, text, start, type) {
    if (!Array.isArray(matches)) return null;
    return matches.find((m) => m && m.type === type && markOffset(text, m) === start) || null;
}

// Non-overlapping [start,end] ranges. Exact validated anchors win; legacy
// signals without one fall back to the match's first occurrence in `text`.
// Ranges that only touch stay separate, so each keeps its own mark (two
// back-to-back search hits are two stops).
export function markRanges(text, matches) {
    if (typeof text !== "string" || !Array.isArray(matches) || matches.length === 0) return [];
    const ranges = [];
    for (const m of matches) {
        const needle = m && m.match;
        if (!needle) continue;
        const idx = markOffset(text, m);
        if (idx !== -1) ranges.push([idx, idx + needle.length, m.type]);
    }
    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const r of ranges) {
        const last = merged[merged.length - 1];
        if (last && r[0] < last[1]) last[1] = Math.max(last[1], r[1]);
        else merged.push(r.slice());
    }
    return merged;
}

// Invisible / zero-width / format code points a mark can land on without having
// anything visible to tint — the unicode-smuggling signal's whole carrier
// vocabulary (zero-widths, bidi controls, variation selectors, the Tag block,
// Hangul/Khmer/Mongolian fillers, interlinear annotation). Presentational only:
// it decides whether the renderer swaps a matched span for a visible placeholder,
// so being broader than the kernel's exact carrier set is harmless — a span only
// reaches this test because a signal already marked it.
const INVISIBLE_RUN_RE = new RegExp(
    "^["
    + "\\u00AD\\u061C\\u115F\\u1160\\u17B4\\u17B5\\u180B-\\u180F"
    + "\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u2064\\u2066-\\u2069"
    + "\\u3164\\uFE00-\\uFE0F\\uFEFF\\uFFA0\\uFFF9-\\uFFFB"
    + "\\u{E0000}-\\u{E007F}\\u{E0100}-\\u{E01EF}"
    + "]+$",
    "u",
);

// True when every code point in `s` is invisible — the span would render as a
// zero-width mark nothing can see, so the renderer shows a placeholder instead.
export function isInvisibleRun(s) {
    return typeof s === "string" && s.length > 0 && INVISIBLE_RUN_RE.test(s);
}
